import { motion } from "framer-motion";
import { Check, Zap, Award, Shield, HeadphonesIcon, Star, ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const plans = [
  {
    name: "Silver IP Voice",
    tagline: "Price-Oriented",
    description: "Ideal for price-oriented services. These routes deliver quality calls without CLI and are continuously monitored by our team for connectivity and stability.",
    features: [
      "Competitive pricing",
      "Continuous monitoring",
      "No CLI delivery",
      "Stable connections",
      "Basic support",
      "Standard routes"
    ],
    cta: "Get Silver Rates",
    highlight: false
  },
  {
    name: "Gold IP Voice",
    tagline: "Quality-Focused",
    description: "Quality-focused retail route ideal for consumer services. We constantly monitor our traffic to ensure we deliver high ASRs, ACDs, and low PDD.",
    features: [
      "High ASR & ACD",
      "Low PDD",
      "Retail quality",
      "CLI delivery",
      "Priority support",
      "Real-time monitoring"
    ],
    cta: "Get Gold Rates",
    highlight: true,
    badge: "Most Popular"
  },
  {
    name: "Platinum IP Voice",
    tagline: "Highest Quality",
    description: "The highest quality service. We guarantee full CLI delivery and retail quality calls through the connections of our Tier 1 and Direct partnerships around the world.",
    features: [
      "Full CLI delivery",
      "Tier 1 partnerships",
      "Direct connections",
      "Premium quality",
      "24/7 VIP support",
      "Dedicated routes"
    ],
    cta: "Get Platinum Rates",
    highlight: false
  }
];

const callCenterPlan = {
  name: "Call Center IP Voice",
  tagline: "Specialized for Call Centers",
  description: "We have developed our call center route to address the challenges in providing call center traffic that has low average call duration (ACD) and high call per minute (CPM). We have engineered this route to only use carriers who have shown a tolerance for this type of traffic.",
  features: [
    "Engineered for low ACD",
    "High CPM tolerance",
    "Selected carrier network",
    "Optimized for call centers",
    "Competitive rates",
    "Dedicated monitoring"
  ]
};

const PricingPage = () => {
  return (
    <div data-testid="pricing-page">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20 noise-bg" data-testid="pricing-hero">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-red-600/20 text-red-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              Transparent Pricing
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-4">Choose Your Plan</h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              From budget-friendly to premium quality - we have the right solution for your business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="section-light -mt-12 relative z-10" data-testid="pricing-cards">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, i) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`relative flex flex-col p-8 rounded-2xl bg-white border transition-all ${
                  plan.highlight 
                    ? 'border-red-500 border-2 shadow-2xl scale-105 z-10' 
                    : 'border-slate-200 shadow-lg hover:shadow-xl hover:-translate-y-1'
                }`}
                data-testid={`pricing-card-${plan.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-red-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                    {plan.badge}
                  </div>
                )}
                
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-slate-900">{plan.name}</h3>
                  <p className="text-red-600 font-medium">{plan.tagline}</p>
                </div>
                
                <p className="text-slate-600 mb-6 flex-grow">{plan.description}</p>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-3">
                      <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-red-600" />
                      </div>
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Link to="/rates" className="mt-auto">
                  <Button 
                    className={`w-full rounded-full py-6 font-semibold ${
                      plan.highlight 
                        ? 'bg-red-600 hover:bg-red-700 text-white' 
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                    }`}
                  >
                    {plan.cta} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call Center Plan */}
      <section className="section-alt" data-testid="call-center-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <HeadphonesIcon className="w-4 h-4" />
                Specialized Solution
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {callCenterPlan.name}
              </h2>
              <p className="text-lg text-slate-600 mb-6">{callCenterPlan.description}</p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                {callCenterPlan.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-red-600" />
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-4">
                <Link to="/rates">
                  <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6">
                    View Rates
                  </Button>
                </Link>
                <a href="https://t.me/cyberx007" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="rounded-full px-6 border-slate-300">
                    <MessageCircle className="mr-2 w-4 h-4" /> Contact Sales
                  </Button>
                </a>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1680992046617-e2e35451bcdb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxzZXJ2ZXIlMjByb29tJTIwcmVkJTIwbGlnaHRzfGVufDB8fHx8MTc3MTE3MjUwNHww&ixlib=rb-4.1.0&q=85"
                alt="Server Infrastructure"
                className="w-full h-[400px] object-cover rounded-2xl shadow-xl grayscale"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent rounded-2xl" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="section-light" data-testid="features-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">All Plans Include</h2>
            <p className="text-slate-600">Core features available across all pricing tiers</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "Secure Infrastructure", desc: "Enterprise-grade security" },
              { icon: Zap, title: "Real-time Updates", desc: "Instant rate changes" },
              { icon: HeadphonesIcon, title: "24/7 Support", desc: "Via Telegram" },
              { icon: Star, title: "No Setup Fees", desc: "Start immediately" }
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6"
              >
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{feature.title}</h3>
                <p className="text-slate-500 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-dark" data-testid="pricing-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Start?</h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto">
              Create your account and start terminating calls within minutes. Minimum recharge: 100 USD/EUR.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/portal" rel="noopener noreferrer">
                <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8 py-6 text-lg">
                  Create Account <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <Link to="/referral">
                <Button variant="outline" className="rounded-full px-8 py-6 text-lg border-slate-600 text-white hover:bg-white/10">
                  <Award className="mr-2 w-5 h-5" /> Referral Program
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
